import React from "react";

/**
 * Price — Illustrations custom pour l'onboarding.
 *
 * Style : Neubrutalism cartoon / street — contours noirs épais (2.5-3px),
 * remplissages plats, petites étincelles noires, look "sticker".
 * Palette pilotée par le slide (accent + bg passés en prop).
 *
 * Chaque composant est une SVG viewBox 200×200 conçue pour être
 * scale-libre (le parent contrôle la taille via className).
 */

const STROKE = "#000";
const STROKE_W = 3;

/* ------------------------------------------------------------------ */
/* Illustration 1 — Covoiturage : 2 potes dans une petite voiture      */
/* ronde avec un cœur au-dessus.                                       */
/* ------------------------------------------------------------------ */
export function IllustrationCarpool({ accent = "#FF3B3B", bg = "#FFE5E5" }) {
  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Ground line — dashed road */}
      <line
        x1="20" y1="165" x2="180" y2="165"
        stroke={STROKE} strokeWidth={STROKE_W}
        strokeDasharray="6 8" strokeLinecap="round"
      />

      {/* Little heart floating above — "sharing" symbol */}
      <g transform="translate(100 30)">
        <path
          d="M0 6 Q-6 -4 -10 2 Q-14 8 0 20 Q14 8 10 2 Q6 -4 0 6 Z"
          fill={accent} stroke={STROKE} strokeWidth={2}
          strokeLinejoin="round"
        />
        {/* twinkle */}
        <path d="M-18 0 l3 -3 M-18 0 l3 3" stroke={STROKE} strokeWidth={2} strokeLinecap="round" />
        <path d="M18 6 l3 -3 M18 6 l3 3" stroke={STROKE} strokeWidth={2} strokeLinecap="round" />
      </g>

      {/* Car body — chunky rounded silhouette */}
      <g>
        {/* Main body */}
        <path
          d="M35 130 L35 100 Q35 88 50 80 L65 65 Q72 58 85 58 L115 58 Q128 58 135 65 L150 80 Q165 88 165 100 L165 130 Q165 140 155 140 L45 140 Q35 140 35 130 Z"
          fill={accent} stroke={STROKE} strokeWidth={STROKE_W}
          strokeLinejoin="round"
        />

        {/* Windshield / cabin glass — split into two side windows */}
        <path
          d="M62 78 L72 68 Q77 63 84 63 L98 63 L98 100 L54 100 L54 92 Q54 88 58 84 Z"
          fill="#FFF8F1" stroke={STROKE} strokeWidth={STROKE_W - 0.5} strokeLinejoin="round"
        />
        <path
          d="M138 78 L128 68 Q123 63 116 63 L102 63 L102 100 L146 100 L146 92 Q146 88 142 84 Z"
          fill="#FFF8F1" stroke={STROKE} strokeWidth={STROKE_W - 0.5} strokeLinejoin="round"
        />

        {/* Driver — left window */}
        <g transform="translate(76 84)">
          {/* head */}
          <circle cx="0" cy="0" r="10" fill="#F5C99B" stroke={STROKE} strokeWidth={2} />
          {/* hair swoop */}
          <path d="M-9 -3 Q-6 -12 4 -10 Q9 -8 9 -2" fill={STROKE} />
          {/* smile */}
          <path d="M-3 3 Q0 6 3 3" stroke={STROKE} strokeWidth={2} fill="none" strokeLinecap="round" />
          {/* eye */}
          <circle cx="-3" cy="-1" r="1.2" fill={STROKE} />
          <circle cx="3" cy="-1" r="1.2" fill={STROKE} />
        </g>

        {/* Passenger — right window */}
        <g transform="translate(124 84)">
          <circle cx="0" cy="0" r="10" fill="#E8B48A" stroke={STROKE} strokeWidth={2} />
          {/* short hair */}
          <path d="M-9 -3 Q-5 -11 0 -10 Q5 -11 9 -3" fill={STROKE} />
          <path d="M-3 3 Q0 5 3 3" stroke={STROKE} strokeWidth={2} fill="none" strokeLinecap="round" />
          <circle cx="-3" cy="-1" r="1.2" fill={STROKE} />
          <circle cx="3" cy="-1" r="1.2" fill={STROKE} />
        </g>

        {/* Door split line */}
        <line x1="100" y1="100" x2="100" y2="140" stroke={STROKE} strokeWidth={2} />

        {/* Headlight highlight */}
        <ellipse cx="42" cy="110" rx="4" ry="6" fill="#FFF8F1" stroke={STROKE} strokeWidth={2} />
        <ellipse cx="158" cy="110" rx="4" ry="6" fill={STROKE} />
      </g>

      {/* Wheels */}
      <g>
        <circle cx="60" cy="145" r="16" fill={STROKE} />
        <circle cx="60" cy="145" r="7" fill={bg} stroke={STROKE} strokeWidth={2} />
        <circle cx="140" cy="145" r="16" fill={STROKE} />
        <circle cx="140" cy="145" r="7" fill={bg} stroke={STROKE} strokeWidth={2} />
      </g>

      {/* Speed lines */}
      <g stroke={STROKE} strokeWidth={2.5} strokeLinecap="round">
        <line x1="8" y1="125" x2="20" y2="125" />
        <line x1="12" y1="135" x2="24" y2="135" />
      </g>

      {/* Sparkle */}
      <path d="M175 40 l4 -4 M175 40 l4 4 M175 40 l-4 -4 M175 40 l-4 4" stroke={STROKE} strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Illustration 2 — Passager & covoitureur : deux personnages qui se   */
/* font un check main dans la main, avec bulles rôle au-dessus.        */
/* ------------------------------------------------------------------ */
export function IllustrationRoles({ accent = "#2A9D3F", bg = "#D6F5D6" }) {
  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Ground */}
      <line x1="20" y1="170" x2="180" y2="170" stroke={STROKE} strokeWidth={STROKE_W} strokeLinecap="round" />

      {/* Left character — covoitureur (holding a mini steering wheel bubble) */}
      <g transform="translate(50 100)">
        {/* Speech bubble */}
        <g transform="translate(-20 -60)">
          <rect x="0" y="0" width="42" height="26" rx="10" fill={accent} stroke={STROKE} strokeWidth={2.5} />
          {/* Steering wheel */}
          <circle cx="21" cy="13" r="7" fill="#FFF8F1" stroke={STROKE} strokeWidth={2} />
          <circle cx="21" cy="13" r="1.8" fill={STROKE} />
          <line x1="14" y1="13" x2="28" y2="13" stroke={STROKE} strokeWidth={2} />
          <line x1="21" y1="13" x2="21" y2="20" stroke={STROKE} strokeWidth={2} />
          {/* Tail */}
          <path d="M14 26 L18 32 L22 26 Z" fill={accent} stroke={STROKE} strokeWidth={2} strokeLinejoin="round" />
        </g>

        {/* Body — hoodie style */}
        <path
          d="M-22 40 L-22 10 Q-22 0 -12 -4 L-6 -6 L6 -6 L12 -4 Q22 0 22 10 L22 40 Z"
          fill={accent} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round"
        />
        {/* Head */}
        <circle cx="0" cy="-14" r="14" fill="#F5C99B" stroke={STROKE} strokeWidth={STROKE_W} />
        {/* Hair */}
        <path d="M-13 -14 Q-10 -28 0 -27 Q10 -28 13 -14 L11 -18 Q7 -25 0 -24 Q-7 -25 -11 -18 Z" fill={STROKE} />
        {/* Face */}
        <circle cx="-4" cy="-13" r="1.5" fill={STROKE} />
        <circle cx="4" cy="-13" r="1.5" fill={STROKE} />
        <path d="M-4 -8 Q0 -5 4 -8" stroke={STROKE} strokeWidth={2} fill="none" strokeLinecap="round" />
        {/* Right arm reaching out */}
        <path d="M20 10 Q35 8 42 22" stroke={STROKE} strokeWidth={STROKE_W} strokeLinecap="round" fill="none" />
      </g>

      {/* Right character — passager */}
      <g transform="translate(150 100)">
        {/* Speech bubble — bag icon */}
        <g transform="translate(-22 -60)">
          <rect x="0" y="0" width="42" height="26" rx="10" fill="#FFB800" stroke={STROKE} strokeWidth={2.5} />
          {/* Backpack/bag */}
          <path d="M14 8 L14 6 Q14 4 16 4 L26 4 Q28 4 28 6 L28 8" fill="none" stroke={STROKE} strokeWidth={2} />
          <rect x="12" y="8" width="18" height="12" rx="2" fill="#FFF8F1" stroke={STROKE} strokeWidth={2} />
          <line x1="16" y1="14" x2="26" y2="14" stroke={STROKE} strokeWidth={1.5} />
          {/* Tail */}
          <path d="M20 26 L24 32 L28 26 Z" fill="#FFB800" stroke={STROKE} strokeWidth={2} strokeLinejoin="round" />
        </g>

        {/* Body — different color hoodie */}
        <path
          d="M-22 40 L-22 10 Q-22 0 -12 -4 L-6 -6 L6 -6 L12 -4 Q22 0 22 10 L22 40 Z"
          fill="#FFB800" stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round"
        />
        {/* Head */}
        <circle cx="0" cy="-14" r="14" fill="#E8B48A" stroke={STROKE} strokeWidth={STROKE_W} />
        {/* Hair short */}
        <path d="M-13 -14 Q-8 -26 0 -26 Q8 -26 13 -14 L10 -19 Q4 -22 0 -22 Q-4 -22 -10 -19 Z" fill={STROKE} />
        {/* Face */}
        <circle cx="-4" cy="-13" r="1.5" fill={STROKE} />
        <circle cx="4" cy="-13" r="1.5" fill={STROKE} />
        <path d="M-4 -8 Q0 -5 4 -8" stroke={STROKE} strokeWidth={2} fill="none" strokeLinecap="round" />
        {/* Left arm reaching out */}
        <path d="M-20 10 Q-35 8 -42 22" stroke={STROKE} strokeWidth={STROKE_W} strokeLinecap="round" fill="none" />
      </g>

      {/* Fist bump in the middle */}
      <g transform="translate(100 122)">
        {/* Fist 1 (left) */}
        <circle cx="-9" cy="0" r="9" fill="#F5C99B" stroke={STROKE} strokeWidth={2.5} />
        {/* Fist 2 (right) */}
        <circle cx="9" cy="0" r="9" fill="#E8B48A" stroke={STROKE} strokeWidth={2.5} />
        {/* Impact sparks */}
        <g stroke={STROKE} strokeWidth={2} strokeLinecap="round" fill="none">
          <line x1="0" y1="-14" x2="0" y2="-19" />
          <line x1="-5" y1="-13" x2="-8" y2="-17" />
          <line x1="5" y1="-13" x2="8" y2="-17" />
          <line x1="0" y1="14" x2="0" y2="19" />
        </g>
      </g>

      {/* Bottom sparkle diamonds */}
      <path d="M20 180 l3 -3 3 3 -3 3 z" fill={STROKE} />
      <path d="M175 180 l3 -3 3 3 -3 3 z" fill={STROKE} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Illustration 3 — Cash + carte + petites pièces qui volent           */
/* ------------------------------------------------------------------ */
export function IllustrationPayment({ accent = "#FFB800", bg = "#FFF3CC" }) {
  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Big wallet base */}
      <g transform="translate(30 80)">
        {/* Wallet body */}
        <rect x="0" y="10" width="120" height="80" rx="10" fill={accent} stroke={STROKE} strokeWidth={STROKE_W} />
        {/* Wallet flap on top */}
        <path
          d="M0 20 L0 15 Q0 5 10 5 L110 5 Q120 5 120 15 L120 20"
          fill={accent} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round"
        />
        {/* Button / clasp */}
        <circle cx="105" cy="50" r="7" fill="#FFF8F1" stroke={STROKE} strokeWidth={2.5} />
        <circle cx="105" cy="50" r="2.5" fill={STROKE} />

        {/* Cash bill peeking out */}
        <g transform="translate(15 -12) rotate(-6)">
          <rect x="0" y="0" width="70" height="30" rx="4" fill="#8CE59F" stroke={STROKE} strokeWidth={2.5} />
          <circle cx="35" cy="15" r="7" fill="#FFF8F1" stroke={STROKE} strokeWidth={2} />
          <text x="35" y="19" textAnchor="middle" fontSize="9" fontFamily="Outfit, system-ui" fontWeight="900" fill={STROKE}>€</text>
          {/* corner marks */}
          <text x="8" y="11" fontSize="7" fontFamily="Outfit, system-ui" fontWeight="900" fill={STROKE}>€</text>
          <text x="62" y="27" fontSize="7" fontFamily="Outfit, system-ui" fontWeight="900" fill={STROKE}>€</text>
        </g>
      </g>

      {/* Credit card floating on the right */}
      <g transform="translate(115 40) rotate(15)">
        <rect x="0" y="0" width="70" height="46" rx="6" fill="#FF3B3B" stroke={STROKE} strokeWidth={STROKE_W} />
        {/* Chip */}
        <rect x="8" y="12" width="12" height="9" rx="2" fill="#FFB800" stroke={STROKE} strokeWidth={2} />
        {/* Contactless waves */}
        <path d="M28 14 Q32 17 28 20" stroke={STROKE} strokeWidth={2} fill="none" strokeLinecap="round" />
        <path d="M32 12 Q38 17 32 22" stroke={STROKE} strokeWidth={2} fill="none" strokeLinecap="round" />
        {/* Card number strip */}
        <line x1="8" y1="32" x2="24" y2="32" stroke="#FFF8F1" strokeWidth={3} strokeLinecap="round" />
        <line x1="28" y1="32" x2="44" y2="32" stroke="#FFF8F1" strokeWidth={3} strokeLinecap="round" />
        <line x1="48" y1="32" x2="62" y2="32" stroke="#FFF8F1" strokeWidth={3} strokeLinecap="round" />
        <line x1="8" y1="40" x2="34" y2="40" stroke="#FFF8F1" strokeWidth={2} strokeLinecap="round" />
      </g>

      {/* Floating coins */}
      <g>
        <circle cx="30" cy="55" r="10" fill="#FFB800" stroke={STROKE} strokeWidth={2.5} />
        <text x="30" y="59" textAnchor="middle" fontSize="10" fontFamily="Outfit, system-ui" fontWeight="900" fill={STROKE}>€</text>

        <circle cx="180" cy="120" r="8" fill="#FFB800" stroke={STROKE} strokeWidth={2.5} />
        <text x="180" y="124" textAnchor="middle" fontSize="8" fontFamily="Outfit, system-ui" fontWeight="900" fill={STROKE}>€</text>

        <circle cx="15" cy="130" r="6" fill="#FFB800" stroke={STROKE} strokeWidth={2} />
      </g>

      {/* Motion sparkles */}
      <g stroke={STROKE} strokeWidth={2} strokeLinecap="round" fill="none">
        <path d="M50 30 l0 -8" />
        <path d="M45 35 l-6 0" />
        <path d="M55 35 l6 0" />
      </g>
      <path d="M175 175 l3 -3 3 3 -3 3 z" fill={STROKE} />
      <path d="M20 175 l3 -3 3 3 -3 3 z" fill={STROKE} />
    </svg>
  );
}


/* ------------------------------------------------------------------ */
/* Icône compacte — Scooter/Moto Price style Neubrutalism.             */
/* Prévue pour être utilisée à ~24-40 px (badges, checkbox, chip).     */
/* ------------------------------------------------------------------ */
export function PriceScooterIcon({ size = 32, accent = "#FF3B3B", bg = "#FFF3CC", className = "" }) {
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* Roue arrière */}
      <circle cx="10" cy="30" r="4.5" fill="#111" stroke={STROKE} strokeWidth={2} />
      <circle cx="10" cy="30" r="1.5" fill={bg} />
      {/* Roue avant */}
      <circle cx="30" cy="30" r="4.5" fill="#111" stroke={STROKE} strokeWidth={2} />
      <circle cx="30" cy="30" r="1.5" fill={bg} />
      {/* Plateforme + corps scooter */}
      <path
        d="M8 26 L22 26 L26 20 L30 20 L32 24 L30 26 L28 26"
        fill={accent}
        stroke={STROKE}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* Guidon */}
      <path
        d="M26 20 L26 12 L22 10"
        fill="none"
        stroke={STROKE}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Poignée */}
      <line x1="21" y1="9" x2="24" y2="11" stroke={STROKE} strokeWidth={2.5} strokeLinecap="round" />
      {/* Phare */}
      <circle cx="28" cy="15" r="1.5" fill={bg} stroke={STROKE} strokeWidth={1.5} />
      {/* Petit éclat vitesse */}
      <path
        d="M4 20 l3 0 M4 23 l4 0"
        stroke={STROKE}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Registry so the caller can index by key.                            */
/* ------------------------------------------------------------------ */
export const PriceIllustrations = {
  carpool: IllustrationCarpool,
  roles: IllustrationRoles,
  payment: IllustrationPayment,
};
